import { gql, useApolloClient } from '@apollo/client';
import type { Point } from 'geojson';
import {
  QueryAnyClosestLinkDocument,
  QueryAnyClosestLinkQuery,
  QueryAnyClosestLinkQueryVariables,
  ReusableComponentsVehicleModeEnum,
  ServicePatternScheduledStopPointInsertInput,
  useInsertQuayIntoStopPlaceMutation,
  useInsertStopPointMutation,
} from '../../../../generated/graphql';
import {
  IncompatibleWithExistingRoutesError,
  KnownValueKey,
  LinkNotResolvedError,
  findKeyValue,
  getRouteLabelVariantText,
} from '../../../../utils';
import { parseDate } from '../../../../utils/time';
import { useGetConflictingStops } from '../../../LinesAndRoutes/Common';
import { BrokenRouteCheckParams, CreateChanges, CreateParams } from '../Types';
import { useGetNextQuayPrivateCode } from './useGetNextQuayPrivateCode';
import { useGetRoutesBrokenByStopChange } from './useGetRoutesBrokenByStopChange';
import { useGetStopLinkAndDirection } from './useGetStopLinkAndDirection';

const GQL_QUERY_ANY_CLOSEST_LINK = gql`
  query QueryAnyClosestLink($point: geography!) {
    infrastructure_network_infrastructure_link(
      where: { shape: { _st_d_within: { distance: 100, from: $point } } }
      order_by: []
      limit: 1
    ) {
      infrastructure_link_id
    }
  }
`;

const GQL_INSERT_STOP_POINT = gql`
  mutation InsertStopPoint(
    $stopPoint: service_pattern_scheduled_stop_point_insert_input!
  ) {
    stopPoint: insert_service_pattern_scheduled_stop_point_one(
      object: $stopPoint
    ) {
      scheduled_stop_point_id
      located_on_infrastructure_link_id
      direction
      priority
      measured_location
      label
      point_type
      validity_start
      validity_end
      stop_place_ref
    }
  }
`;

function useCheckForBrokenRoutes() {
  const getRoutesBrokenByStopChange = useGetRoutesBrokenByStopChange();

  // if added stop conflicts with existing routes, warn user.
  // for example, if a stop with same label has already been added to a route,
  // but new stop is not located on that route's geometry, the stop cannot be added
  return async (params: BrokenRouteCheckParams) => {
    const { brokenRoutes } = await getRoutesBrokenByStopChange(params);

    if (brokenRoutes?.length) {
      throw new IncompatibleWithExistingRoutesError(
        brokenRoutes.map((route) => getRouteLabelVariantText(route)).join(', '),
      );
    }
  };
}

export function usePrepareCreate() {
  const getConflictingStops = useGetConflictingStops();
  const getStopLinkAndDirection = useGetStopLinkAndDirection();
  const getNextQuayPrivateCode = useGetNextQuayPrivateCode();

  const checkForBrokenRoutes = useCheckForBrokenRoutes();

  // prepare variables for mutation and validate if it's even allowed
  // try to produce a changeset that can be displayed on an explanatory UI
  return async ({
    stopPoint,
    stopPlaceId,
    quay,
  }: CreateParams): Promise<CreateChanges> => {
    const [conflicts, { closestLink, direction }, privateCodeValue] =
      await Promise.all([
        getConflictingStops({
          // these form values always exist
          label: stopPoint.label,
          priority: stopPoint.priority,
          validityStart: parseDate(stopPoint.validity_start),
          validityEnd: parseDate(stopPoint.validity_end),
        }),

        // we need to fetch the infra link and direction for the stop
        getStopLinkAndDirection({
          stopLocation: stopPoint.measured_location,
          vehicleMode:
            stopPoint.vehicle_mode_on_scheduled_stop_point?.data?.[0]
              ?.vehicle_mode ?? ReusableComponentsVehicleModeEnum.Bus,
        }),

        // next private code
        getNextQuayPrivateCode(),
      ]);

    // check if any routes are broken if this stops is added
    await checkForBrokenRoutes({
      newLink: closestLink,
      newDirection: direction,
      newStop: stopPoint,
      label: stopPoint.label,
      priority: stopPoint.priority,
      stopId: null,
      vehicleMode:
        stopPoint.vehicle_mode_on_scheduled_stop_point?.data?.[0]
          ?.vehicle_mode ?? null,
    });

    const stopPointWithInfraInfo: ServicePatternScheduledStopPointInsertInput =
      {
        ...stopPoint,
        located_on_infrastructure_link_id: closestLink.infrastructure_link_id,
        direction,
      };

    return {
      stopPoint: stopPointWithInfraInfo,
      conflicts,
      stopPlaceId,
      quay: {
        ...quay,
        privateCode: {
          value: privateCodeValue,
          type: 'HSL/JORE-4',
        },
      },
    };
  };
}

export function useCheckIsLocationValidForStop() {
  const apollo = useApolloClient();

  // Checks if the given location is near any infrastructure link at all.
  return async (stopLocation: Point): Promise<true> => {
    const result = await apollo.query<
      QueryAnyClosestLinkQuery,
      QueryAnyClosestLinkQueryVariables
    >({
      query: QueryAnyClosestLinkDocument,
      variables: { point: stopLocation },
    });

    if (!result.data.infrastructure_network_infrastructure_link.length) {
      throw new LinkNotResolvedError(
        result.error,
        `Could not resolve closest link to point ${stopLocation}`,
      );
    }

    return true;
  };
}

export function useCreateStop() {
  const [insertStopPointMutation] = useInsertStopPointMutation();
  const [insertQuayIntoStopPlaceMutation] = useInsertQuayIntoStopPlaceMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      'GetStopPlaceDetails',
      'GetStopInfoForEditingOnMap',
      'GetMapStops',
    ],
  });

  return async ({ stopPlaceId, quay, stopPoint }: CreateChanges) => {
    const insertQuayResult = await insertQuayIntoStopPlaceMutation({
      variables: { stopPlaceId, quayInput: quay },
    });

    const quayId = insertQuayResult.data?.stop_registry?.mutateStopPlace
      ?.at(0)
      ?.quays?.find(
        (it) =>
          it &&
          findKeyValue(it, KnownValueKey.ImportedId) ===
            findKeyValue(quay, KnownValueKey.ImportedId),
      )?.id;

    // This should never be true
    if (!quayId) {
      throw new Error(
        'StopPlace mutation did not fail, but the to be inserted Quay was not found from the response!',
      );
    }

    const insertedStopPointResult = await insertStopPointMutation({
      variables: {
        stopPoint: {
          ...stopPoint,
          stop_place_ref: quayId,
        },
      },
    });

    const stopPointId =
      insertedStopPointResult.data?.stopPoint?.scheduled_stop_point_id;

    // This should never be true
    if (!stopPointId) {
      throw new Error(
        'StopPoint insert did not fail, but the to be inserted StopPoint was not found from the response!',
      );
    }

    return { quayId, stopPointId };
  };
}
